// @flow
import { connectManateeSocket } from './ManateeSocket'
import Event from './Event'

/**
 * Generic socket.
 * @function
 * @namespace MonitorSocket
 * @version 1.0.0
 * @description
 * The monitor-socket is used for communicating directly with a Manatee running on the same machine as Cuesta.
 * @return { Object } Exposes `on`, `off` methods to subscribe to the events described above and `remove` to close the socket connection as well.
 */

/**
 * Represents a monitor-socket.
 * @typedef { Object } MonitorSocket
 * @memberof MonitorSocket
 */
type MonitorSocket = {
  startMonitor(app: Object, mode: string): void,
  highlight(
    app: Object,
    field: string,
    fieldPath: string,
    imageData: string,
    matchConficence: number,
    offsetX: number,
    offsetY: number,
    matchOnDesktop: boolean,
    traversalArgs: Array<string>
  ): void,
  on(name: string, method: Function): void,
  off(name: string, method: Function): void,
  remove(): void
}

function handleSocketMessages(ws, event) {
  ws.onmessage = (msg: any) => {
    try {
      const data = JSON.parse(msg.data)

      event.emit('message', data)
      if (data.Event && data.Event.toLowerCase() === 'selected') event.emit('selected', data.Target)
      if (data.Event && data.Event.toLowerCase() === 'offsetselected')
        event.emit('offsetselected', data)
      if (data.Event && data.Event.toLowerCase() === 'hover') event.emit('hover', data.Target)
      if (data.Event && data.Event.toLowerCase() === 'initialized') event.emit('initialized')
      if (data.Error || data.ErrorCode) event.emit('errormessage', data)
    } catch (e) {
      console.warn('Received unintelligible monitor socket message: ', msg)
    }
  }
}

export default (): MonitorSocket => {
  const event = new Event()
  let ws
  let closed = false
  connectManateeSocket('monitor', w => (ws = w), event, null, null, null, () => closed)

  event.on('open', () => handleSocketMessages(ws, event))

  const remove = () => ((closed = true), ws.close(), event.reset())

  const highlight = (
    app: Object,
    field: string,
    fieldPath: string,
    imageData: string,
    matchConficence: number,
    offsetX: number,
    offsetY: number,
    matchOnDesktop: boolean,
    traversalArgs: Array<string>
  ) => {
    ws.send(
      JSON.stringify({
        Action: 'highlight-field',
        Field: field,
        FieldPath: fieldPath,
        FieldImage: imageData,
        FieldImageConfidence: matchConficence,
        FieldImageOffsetX: offsetX,
        FieldImageOffsetY: offsetY,
        FieldImageMatchOnDesktop: matchOnDesktop,
        TraversalArgs: traversalArgs || [],
        ApplicationConfiguration: {
          Name: app.name,
          Identifier: app.id,
          Descriptive: { ...app }
        }
      })
    )
  }

  const startMonitor = (app: Object, mode: string) => {
    ws.send(
      JSON.stringify({
        Action: 'monitor-start',
        Mode: mode,
        ApplicationConfiguration: {
          Name: app.name,
          Identifier: app.id,
          Descriptive: { ...app }
        }
      })
    )
  }

  return {
    on: event.on,
    off: event.off,
    highlight,
    startMonitor,
    remove
  }
}
